import {
  put, call, select,
} from 'lib/redux';
import i18n from 'i18n';
import history from 'store/CreateHistory';
import * as types from 'constants/ActionTypes';
import placeOrderService from 'services/placeOrder.service';
import {
  COMMENTS_MAX_LENGTH,
  EMAIL_REGEX, CLIENT_ERROR_RESPONSES,
  CREDIT_CARD_NAMES,
  PRODUCT_IDS,
  USER_EVENTS,
  NEW_UNIFIED_SKUS,
} from 'constants/AppConstants';
import { ORDERS_SUBMITTED_ROUTE } from 'constants/RouteConstants';
import { showCardDetails } from 'features/finalizeOrder/components/paymentSection/PaymentSection.util';
import { validatePaymentInfo } from 'commonComponents/paymentInformation/PaymentInformation.util';
import {
  placeOrderSelector,
  paymentInfoSelector,
  validateClaimInfoInputs,
  showInsuredNameField,
  orderHasCarrierEstimate,
  getNewSKUSLAEstimatedDeliveryTimeMessage,
} from 'features/finalizeOrder/FinalizeOrder.utils';
import { getBillingInformationSaga } from 'features/userData/UserData.saga';
import updateBillPayerInfoService from 'services/updateBillPayerInfo.service';
import { getCardDataFromPaymentInfo } from 'features/billingInfomation/BillingInformation.util';
import { loadUpgradedReportDetailsSaga } from 'features/finalizeOrder/FinalizeOrder.saga';
import { logError } from 'utils/logger.utils';
import { trackUserEvent } from 'utils/app.utils';
import { featureFlagSelector, isSubscriptionDashboardV2EnabledSelector } from 'utils/appSelectors.utils';
import { UNIFIED_SKU_SLA_AREA_LAND_USE } from 'constants/FeatureFlag.constants';

export function* validateCreditCard({ transientTokenData }) {
  try {
    const request = yield select(paymentInfoSelector);

    const cardData = {
      firstName: request.firstName,
      lastName: request.lastName,
      expMonth: request.expirationDateMonth,
      expYear: request.expirationDateYear,
      cardName: CREDIT_CARD_NAMES[request.cardType],
      ...(transientTokenData.isCardNumberUpdated && { transientToken: transientTokenData.transientToken }),
    };

    yield put({ type: types.CYBERSOURCE_TOKEN_SUCCESS, cardData, ccConsent: request.consent });
  } catch (error) {
    logError({
      message: 'Error in validateCreditCard',
      error: error.message,
      errorCode: error.errorCode,
    });
    yield put({ type: types.CYBERSOURCE_TOKEN_ERROR, error: error.message });
  }
}

export function* validatePaymentInfoSaga({ transientTokenData }) {
  const { t } = i18n;
  try {
    const {
      billingInformation: { accountBalance, isTermsUser, hasAdministerBillingPermission },
      estimatedCostResponse,
      paymentInfo: { ...paymentInfo },
    } = yield select(({ finalizeOrder, placeOrder, userData: { billingInformation } }) => ({
      ...finalizeOrder, emailCC: placeOrder.emailCC, comments: placeOrder.comments, productSets: placeOrder.productSets, billingInformation,
    }));

    const estimatedTotal = estimatedCostResponse ? estimatedCostResponse.maxTotal : 0;
    const cardDetailsVisible = showCardDetails(hasAdministerBillingPermission, accountBalance > 0, estimatedTotal, accountBalance, isTermsUser);
    if (cardDetailsVisible) {
      const paymentValidations = validatePaymentInfo(paymentInfo, t, transientTokenData);
      yield put({ type: types.UPDATE_PAYMENT_INFO_ERRORS, paymentInfoErrors: paymentValidations });
      return paymentValidations;
    }

    return null;
  } catch (error) {
    logError({
      message: 'Error in validatePaymentInfoSaga',
      error: error.message,
      errorCode: error.errorCode,
    });
    return null;
  }
}

export function* validateClaimInfoSaga() {
  const { t } = i18n;
  const {
    claimInfo,
    validatorFields,
    controlStates,
    productSets,
  } = yield select(({ finalizeOrder, placeOrder, userData }) => ({
    ...finalizeOrder,
    emailCC: placeOrder.emailCC,
    comments: placeOrder.comments,
    productSets: placeOrder.productSets,
    controlStates: userData.controlStates,
  }));
  const claimInfoErrors = validateClaimInfoInputs(claimInfo, validatorFields);

  const displayInsuredName = showInsuredNameField(controlStates, productSets);
  const displayHomeowner = controlStates?.homeOwner && !controlStates?.homeOwner.hidden;

  // Homeowner is required if it is displayed. InsuredName is required if it is displayed and the order does not have Carrier Estimate
  if ((displayHomeowner || (displayInsuredName && !orderHasCarrierEstimate(productSets))) && !claimInfo.insuredName) {
    claimInfoErrors.insuredName = t(
      'common__field_is_required',
      { field: t(displayHomeowner ? 'finalize_order__claim_information__homeowner' : 'finalize_order__claim_information__insured_name') },
    );
  }

  claimInfoErrors.dateOfLoss = claimInfoErrors.dateOfLoss
    || ((new Date(claimInfo.dateOfLoss) > new Date()) ? t('finalize_order__claim_information__dateofloss_error') : '');

  claimInfoErrors.deductible = (Number.isNaN(Number(claimInfo.deductible))) ? t('finalize_order__claim_information__deductible_error') : '';

  yield put({ type: types.UPDATE_CLAIM_INFO_ERRORS, claimInfoErrors });
  const hasClaimInfoErrors = Object.values(claimInfoErrors).some((error) => error.length > 0);

  if (hasClaimInfoErrors) yield put({ type: types.EXPAND_PANEL, isExpanded: true, panelName: 'claimInformation' });

  return hasClaimInfoErrors;
}

const hasInputValidationErrors = ({
  paymentValidations,
  hasInvalidEmailIDs,
  commentsLengthExceeded,
  hasClaimInfoErrors,
}) => (
  Boolean(paymentValidations?.isError)
  || hasInvalidEmailIDs
  || commentsLengthExceeded
  || hasClaimInfoErrors
);

function* validateOrderBeforeSubmission(transientTokenData) {
  const {
    paymentInfo: {
      isUpdated: isPaymentInfoUpdated,
    },
    emailCC,
    comments,
    bluePrintFileId,
  } = yield select(({ finalizeOrder, placeOrder }) => ({
    paymentInfo: finalizeOrder.paymentInfo,
    emailCC: placeOrder.emailCC,
    comments: placeOrder.comments,
    bluePrintFileId: placeOrder.bluePrintFileId,
  }));

  const { t } = i18n;
  const paymentValidations = yield validatePaymentInfoSaga({ transientTokenData });
  const hasInvalidEmailIDs = !(emailCC.every((value) => EMAIL_REGEX.test(value)));
  const commentsLengthExceeded = comments.length > COMMENTS_MAX_LENGTH;
  yield put({ type: types.EXPAND_PANEL, isExpanded: hasInvalidEmailIDs, panelName: 'emailRecipients' });

  // Validate claim info fields only for non-blueprint orders.
  const hasClaimInfoErrors = bluePrintFileId ? false : yield validateClaimInfoSaga();
  const hasValidationErrors = hasInputValidationErrors({
    paymentValidations,
    hasInvalidEmailIDs,
    commentsLengthExceeded,
    hasClaimInfoErrors,
  });

  if (isPaymentInfoUpdated && !hasValidationErrors && !transientTokenData.transientToken) {
    yield put({
      type: types.UPDATE_PAYMENT_INFO_ERRORS,
      paymentInfoErrors: {
        firstName: '',
        lastName: '',
        cardNumber: t('payment_information__card_validation'),
        cardType: '',
        expirationDateMonth: '',
        expirationDateYear: '',
        isError: true,
      },
    });
    yield put({ type: types.PLACE_ORDER_STOP_LOADING });
    return { canProceed: false, isPaymentInfoUpdated };
  }

  if (hasValidationErrors) {
    yield put({ type: types.PLACE_ORDER_STOP_LOADING });
    return { canProceed: false, isPaymentInfoUpdated };
  }

  return { canProceed: true, isPaymentInfoUpdated };
}

function* syncBillingInfoIfUpdated(isPaymentInfoUpdated, transientTokenData) {
  if (!isPaymentInfoUpdated) return;

  const paymentInfo = yield select(({ finalizeOrder }) => finalizeOrder.paymentInfo);
  const cardData = getCardDataFromPaymentInfo(paymentInfo);

  const billingUpdateResponse = yield call(updateBillPayerInfoService, {
    cardData: { ...cardData, transientToken: transientTokenData.transientToken },
    saveCard: true,
  });

  if (!billingUpdateResponse.ok) {
    const error = new Error(billingUpdateResponse.json?.errorDescription || 'Failed to update billing information');
    error.errorCode = billingUpdateResponse.json?.errorCode;
    throw error;
  }

  yield call(getBillingInformationSaga, { isUpdated: true });
}

export function* getEstimatedCostForSubmittedOrder() {
  const {
    apiEstimatedCost,
    upgradeFromReportId,
  } = yield select(({ finalizeOrder }) => ({
    apiEstimatedCost: finalizeOrder?.estimatedCostResponse?.total?.cost,
    upgradeFromReportId: finalizeOrder?.upgradeFromReportId,
  }));
  const isSubscriptionDashboardV2Enabled = yield select(isSubscriptionDashboardV2EnabledSelector);
  const {
    selectedPrimaryProduct,
    primarySubscriptionProductsWithUsage,
    subscriptionEstimatedTotal,
  } = yield select(({ order }) => ({
    selectedPrimaryProduct: order.selectedPrimaryProduct,
    primarySubscriptionProductsWithUsage: order.primarySubscriptionProductsWithUsage ?? [],
    subscriptionEstimatedTotal: order.subscriptionEstimatedTotal,
  }));

  const isSelectedPrimarySubscriptionProduct = Boolean(
    selectedPrimaryProduct?.ProductID
    && primarySubscriptionProductsWithUsage.some(
      (p) => Number(p.productId) === Number(selectedPrimaryProduct.ProductID) && p.isSubscription,
    ),
  );

  // #genai Upgrade-from-history does not set the order-page product, so the local upgrade total
  // (1 Full House unit, including overage) must be used whenever it was calculated.
  const isSubscriptionUpgrade = Boolean(upgradeFromReportId && subscriptionEstimatedTotal);

  if (isSubscriptionDashboardV2Enabled
    && subscriptionEstimatedTotal
    && (isSelectedPrimarySubscriptionProduct || isSubscriptionUpgrade)) {
    return subscriptionEstimatedTotal;
  }

  return apiEstimatedCost;
}

export function* enrichPlaceOrderResponse(baseResponse) {
  const isComplex = yield select(({ order }) => order.isComplex);
  const propertyType = yield select(({ order }) => order.propertyType);
  const mapPoints = yield select(({ orderAddresses }) => orderAddresses.mapPoints);
  const focusedPointIndex = yield select(({ orderAddresses }) => orderAddresses.focusedPointIndex);
  const estimatedCost = yield call(getEstimatedCostForSubmittedOrder);
  const upgradeFromReportId = yield select(({ finalizeOrder }) => finalizeOrder?.upgradeFromReportId);
  const bluePrintId = yield select(({ placeOrder }) => placeOrder.bluePrintId);

  const isPropertyDataSelected = yield select(
    ({ placeOrder }) => placeOrder.productSets?.some(
      (productSet) => productSet.primaryProductId === PRODUCT_IDS.PropertyData,
    ),
  );
  const unifiedSkuSlaAreaLandUseFlag = yield select(featureFlagSelector(UNIFIED_SKU_SLA_AREA_LAND_USE));

  const orderDetails = unifiedSkuSlaAreaLandUseFlag && baseResponse.orderDetails
    ? baseResponse.orderDetails.map((orderDetail) => {
      const isNewSKU = NEW_UNIFIED_SKUS.includes(orderDetail.productID);
      const delivery = orderDetail.delivery.toLowerCase();
      let deliveryType = '';
      if (delivery.includes('regular')) deliveryType = 'Regular';
      else if (delivery.includes('quick')) deliveryType = 'Quick';
      if (!isNewSKU || !['Regular', 'Quick'].includes(deliveryType)) return orderDetail;
      return {
        ...orderDetail,
        ...getNewSKUSLAEstimatedDeliveryTimeMessage(orderDetail.productID, deliveryType) || {},
      };
    })
    : baseResponse.orderDetails;

  const reportDetails = isPropertyDataSelected && baseResponse.reportDetails
    ? baseResponse.reportDetails.map((reportDetail) => ({
      ...reportDetail,
      delivery: reportDetail.product === 'Property Data' ? 'Instant' : reportDetail.delivery,
    }))
    : baseResponse.reportDetails;

  const useEmbeddedExplorer = yield select(({ order }) => order.useEmbeddedExplorer);

  return {
    ...baseResponse,
    isComplex,
    propertyType,
    mapPoints,
    focusedPointIndex,
    estimatedCost,
    // #genai Submitted page uses this so a Full House upgrade stays 1 unit, not the new-order badge of 2.
    isUpgradeOrder: Boolean(upgradeFromReportId),
    bluePrintId,
    orderDetails,
    reportDetails,
    ...(isPropertyDataSelected ? { isPropertyDataOrder: true } : {}),
    useEmbeddedExplorer,
  };
}

function* placeOrderRequestSaga() {
  const placeOrderRequestData = yield select(placeOrderSelector);
  const requestBody = {
    ...placeOrderRequestData,
    cardData: null,
  };
  const productId = yield select(({ finalizeOrder }) => finalizeOrder.productId);
  trackUserEvent(productId ? USER_EVENTS.UPGRADE_ORDER : USER_EVENTS.PLACE_ORDER);

  if (productId === null) {
    delete requestBody.upgradeFromReportId;
    delete requestBody.upgradeFromProduct;
    delete requestBody.upgradeFromMso;
  }

  const response = yield call(placeOrderService, requestBody);
  if (response.status === CLIENT_ERROR_RESPONSES.STATUS_CONFLICT) {
    yield call(loadUpgradedReportDetailsSaga, requestBody.upgradeReportId);
    return null;
  }
  if (!response.ok) {
    const error = new Error(response.json.errorDescription);
    error.errorCode = response.json.errorCode;
    throw error;
  }

  return response.json;
}

export function* placeOrderSaga({ transientTokenData }) {
  try {
    trackUserEvent(USER_EVENTS.PLACE_ORDER);
    yield put({ type: types.PLACE_ORDER_LOADING });
    const { canProceed, isPaymentInfoUpdated } = yield call(validateOrderBeforeSubmission, transientTokenData);
    if (!canProceed) return;
    yield call(syncBillingInfoIfUpdated, isPaymentInfoUpdated, transientTokenData);

    const paymentInfoErrors = yield select(({ finalizeOrder }) => finalizeOrder.paymentInfoErrors);
    if (!paymentInfoErrors.isError) {
      const placeOrderResponse = yield call(placeOrderRequestSaga);
      if (!placeOrderResponse) return;
      const enrichedPlaceOrderResponse = yield call(enrichPlaceOrderResponse, placeOrderResponse);
      yield put({ type: types.PLACE_ORDER_SUCCESS, placeOrderResponse: enrichedPlaceOrderResponse });
      if (history.unblock) {
        history.unblock();
        delete history.unblock;
      }
      history.push(ORDERS_SUBMITTED_ROUTE);
      yield call(getBillingInformationSaga, { isUpdated: true });
    }
  } catch (error) {
    logError({
      message: 'Error in placeOrderSaga',
      error: error.message,
      errorCode: error.errorCode,
    });
    yield put({ type: types.PLACE_ORDER_ERROR, error: error.message });
  }
}
